import CloseIcon from "@mui/icons-material/Close";
import { Alert, IconButton } from "@mui/material";
import * as React from "react";

import { RobotoTypography } from "@/shared/components";
import { VisibilityToggle } from "@/shared/components/VisibilityToggle";
import { getGeoMessagePaths } from "@/shared/components/visualization/Panel/MapPanel/geoTypes";
import {
  isLatitudeType,
  isLongitudeType,
  MessagePathNode,
  PathParser,
} from "@/shared/domain/topics";
import { actions, useVizDispatch } from "@/shared/state/visualization";
import { MapPath, TopicData } from "@/shared/state/visualization/schema/v1";

import { DataSourceSelector } from "../../controls/DataSourceSelector";

import styles from "./Paths.module.css";

interface PathsProps {
  panelId: string;
  paths: MapPath[];
}

export function Paths({ panelId, paths }: PathsProps) {
  // Construct a cache of the path ids for quick lookup when checking for duplicates
  const pathCache = React.useMemo(() => {
    return new Map(
      paths.map((path) => {
        const { latTopicData, lonTopicData } = getGeoMessagePaths(path);
        const latId = latTopicData.messagePath.id;
        const lonId = lonTopicData.messagePath.id;
        return [`lat:${latId}, lon:${lonId}`, path.id];
      }),
    );
  }, [paths]);

  if (paths.length === 0) {
    return null;
  }

  const isDuplicatePath =
    (currentPath: MapPath) => (nextTopicData: TopicData[]) => {
      const id = `lat:${nextTopicData[0].messagePath.id}, lon:${nextTopicData[1].messagePath.id}`;
      return pathCache.has(id) && pathCache.get(id) !== currentPath.id;
    };

  return (
    <div className={styles.paths}>
      {paths.map((path, i) => {
        return (
          <Path
            key={path.id}
            path={path}
            panelId={panelId}
            pathname={`Path ${i + 1}`}
            isDuplicatePath={isDuplicatePath(path)}
          />
        );
      })}
    </div>
  );
}

interface PathProps {
  panelId: string;
  path: MapPath;
  pathname: string;
  isDuplicatePath: (nextTopicData: TopicData[]) => boolean;
}

function Path({ panelId, path, pathname, isDuplicatePath }: PathProps) {
  return (
    <div className={styles.path}>
      <div className={styles.pathHeader}>
        <RobotoTypography variant="overline">{pathname}</RobotoTypography>
        <PathControls panelId={panelId} path={path} />
      </div>
      <PathData
        panelId={panelId}
        path={path}
        isDuplicatePath={isDuplicatePath}
      />
    </div>
  );
}

interface PathControlsProps {
  panelId: string;
  path: MapPath;
}

function PathControls({ panelId, path }: PathControlsProps) {
  const dispatch = useVizDispatch();

  const onRemovePath = (path: MapPath) => {
    dispatch(actions.removePathFromMapPanel(panelId, path.id));
  };
  const onShowPath = (path: MapPath) => {
    dispatch(actions.setPathVisibility(panelId, path.id, !path.visible));
  };

  return (
    <div>
      <VisibilityToggle
        className={styles.iconButton}
        on={path.visible}
        onToggle={() => onShowPath(path)}
      />
      <IconButton
        aria-label="remove"
        className={styles.iconButton}
        onClick={() => onRemovePath(path)}
        title="Remove Path"
      >
        <CloseIcon fontSize="inherit" />
      </IconButton>
    </div>
  );
}

interface PathDataProps {
  panelId: string;
  path: MapPath;
  isDuplicatePath: (nextTopicData: TopicData[]) => boolean;
}

function PathData({ panelId, path, isDuplicatePath }: PathDataProps) {
  const dispatch = useVizDispatch();
  const [error, setError] = React.useState<string | undefined>(undefined);

  const { latTopicData: latitudeTopicData, lonTopicData: longitudeTopicData } =
    getGeoMessagePaths(path);

  const latitude = {
    fileId: latitudeTopicData.topic.association.association_id,
    path: [
      latitudeTopicData.topic.name,
      MessagePathNode.partsToString(latitudeTopicData.messagePath.parts),
    ].join(PathParser.TOPIC_SEPARATOR),
  };

  const longitude = {
    fileId: longitudeTopicData.topic.association.association_id,
    path: [
      longitudeTopicData.topic.name,
      MessagePathNode.partsToString(longitudeTopicData.messagePath.parts),
    ].join(PathParser.TOPIC_SEPARATOR),
  };

  const isLatitude = (messagePathNode: MessagePathNode) => {
    return isLatitudeType(messagePathNode.data.canonical_data_type);
  };

  const isLongitude = (messagePathNode: MessagePathNode) => {
    return isLongitudeType(messagePathNode.data.canonical_data_type);
  };

  return (
    <div className={styles.dataSources}>
      {error && <Alert severity="error">{error}</Alert>}
      <div>
        <RobotoTypography component={"label"} variant="subtitle2">
          Latitude
        </RobotoTypography>
        <DataSourceSelector
          initialFile={latitude.fileId}
          initialPath={latitude.path}
          onChange={(latitudeMessagePathNode) => {
            const nextTopicData = [
              latitudeMessagePathNode.toTopicData(),
              longitudeTopicData,
            ];

            // Disallow editing data to create a duplicate path
            if (isDuplicatePath(nextTopicData)) {
              setError("Path already exists");
              return;
            }

            dispatch(
              actions.setDataSource({
                topicData: nextTopicData,
                panelId,
                segmentId: path.id,
              }),
            );
            setError(undefined);
          }}
          optionFilter={isLatitude}
        />
      </div>
      <div>
        <RobotoTypography component={"label"} variant="subtitle2">
          Longitude
        </RobotoTypography>
        <DataSourceSelector
          initialFile={longitude.fileId}
          initialPath={longitude.path}
          onChange={(longitudeMessagePathNode) => {
            const nextTopicData = [
              latitudeTopicData,
              longitudeMessagePathNode.toTopicData(),
            ];

            // Disallow editing data to create a duplicate path
            if (isDuplicatePath(nextTopicData)) {
              setError("Path already exists");
              return;
            }

            dispatch(
              actions.setDataSource({
                topicData: nextTopicData,
                panelId,
                segmentId: path.id,
              }),
            );
            setError(undefined);
          }}
          optionFilter={isLongitude}
        />
      </div>
    </div>
  );
}
